import { useState } from 'react';
import { useAuditData } from '@/hooks/useAuditData';
import { getScoreColor } from '@/lib/utils';
import { Loader2, CheckCircle2, XCircle, Download, ArrowUpDown, Trophy, AlertTriangle } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';

type SortKey = 'name' | 'performance' | 'seo' | 'accessibility' | 'tracking';
type SortOrder = 'asc' | 'desc';

export function Comparison() {
  const { data, loading, error } = useAuditData();
  const [sortKey, setSortKey] = useState<SortKey>('performance');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const getSortValue = (site: any, key: SortKey): number | string => {
    switch (key) {
      case 'name': return site.name;
      case 'performance': return site.vitals.performanceScore;
      case 'seo': return site.seo.score;
      case 'accessibility': return site.vitals.accessibilityScore;
      case 'tracking': return site.tracking.events?.trackingCoverage?.overallScore || 0;
      default: return 0;
    }
  };

  const sortedSites = [...data.sites].sort((a, b) => {
    const aVal = getSortValue(a, sortKey);
    const bVal = getSortValue(b, sortKey);
    const multiplier = sortOrder === 'asc' ? 1 : -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * multiplier;
    }
    return ((aVal as number) - (bVal as number)) * multiplier;
  });

  const performanceData = data.sites
    .map((site) => ({
      name: site.name.length > 12 ? site.name.substring(0, 12) + '...' : site.name,
      fullName: site.name,
      score: site.vitals.performanceScore,
    }))
    .sort((a, b) => b.score - a.score);

  const seoData = data.sites
    .map((site) => ({
      name: site.name.length > 12 ? site.name.substring(0, 12) + '...' : site.name,
      fullName: site.name,
      score: site.seo.score,
    }))
    .sort((a, b) => b.score - a.score);

  // Radar chart data (first 5 sites max for readability)
  const radarData = [
    { metric: 'Performance', ...Object.fromEntries(data.sites.slice(0, 5).map(s => [s.name, s.vitals.performanceScore])) },
    { metric: 'SEO', ...Object.fromEntries(data.sites.slice(0, 5).map(s => [s.name, s.seo.score])) },
    { metric: 'Accessibility', ...Object.fromEntries(data.sites.slice(0, 5).map(s => [s.name, s.vitals.accessibilityScore])) },
    { metric: 'Best Practices', ...Object.fromEntries(data.sites.slice(0, 5).map(s => [s.name, s.vitals.bestPracticesScore])) },
    { metric: 'Tracking', ...Object.fromEntries(data.sites.slice(0, 5).map(s => [s.name, s.tracking.events?.trackingCoverage?.overallScore || 0])) },
  ];

  const radarColors = ['#22c55e', '#3b82f6', '#f97316', '#a855f7', '#ec4899'];

  const getBarColor = (score: number) => {
    if (score >= 90) return '#22c55e';
    if (score >= 50) return '#f97316';
    return '#ef4444';
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Site', 'Performance', 'SEO', 'Accessibility', 'Best Practices', 'GTM', 'GA4', 'FB Pixel', 'Tracking Score', 'Recommendations'];
    const rows = data.sites.map(site => [
      site.name,
      site.vitals.performanceScore,
      site.seo.score,
      site.vitals.accessibilityScore,
      site.vitals.bestPracticesScore,
      site.tracking.gtm.present ? 'Yes' : 'No',
      site.tracking.ga4.present ? 'Yes' : 'No',
      site.tracking.facebookPixel.present ? 'Yes' : 'No',
      site.tracking.events?.trackingCoverage?.overallScore || 'N/A',
      site.recommendations.length,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `site-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-16 py-8">
      {/* Header */}
      <header className="space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">Comparison</p>
            <h1 className="text-4xl font-light">Side by Side</h1>
            <p className="text-muted-foreground">
              Compare performance across all {data.sites.length} sites
            </p>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors no-print"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </header>

      {/* Rankings Table */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Site Rankings</h2>
        <div className="bg-secondary/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-medium text-muted-foreground">Rank</th>
                <SortableHeader label="Site" sortKey="name" currentKey={sortKey} onSort={handleSort} />
                <SortableHeader label="Performance" sortKey="performance" currentKey={sortKey} onSort={handleSort} />
                <SortableHeader label="SEO" sortKey="seo" currentKey={sortKey} onSort={handleSort} />
                <SortableHeader label="Accessibility" sortKey="accessibility" currentKey={sortKey} onSort={handleSort} />
                <SortableHeader label="Tracking" sortKey="tracking" currentKey={sortKey} onSort={handleSort} />
                <th className="text-center p-4 font-medium text-muted-foreground">Issues</th>
              </tr>
            </thead>
            <tbody>
              {sortedSites.map((site, index) => {
                const trackingScore = site.tracking.events?.trackingCoverage?.overallScore;
                const criticalCount = site.recommendations.filter((r: any) => r.priority === 1).length;
                return (
                  <tr key={site.id} className="border-b border-border/50 hover:bg-secondary/20">
                    <td className="p-4">
                      {index === 0 && sortOrder === 'desc' && sortKey !== 'name' ? (
                        <Trophy className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <span className="text-muted-foreground">{index + 1}</span>
                      )}
                    </td>
                    <td className="p-4 font-medium">{site.name}</td>
                    <td className={`text-center p-4 font-medium ${getScoreColor(site.vitals.performanceScore)}`}>
                      {site.vitals.performanceScore}
                    </td>
                    <td className={`text-center p-4 font-medium ${getScoreColor(site.seo.score)}`}>
                      {site.seo.score}
                    </td>
                    <td className={`text-center p-4 font-medium ${getScoreColor(site.vitals.accessibilityScore)}`}>
                      {site.vitals.accessibilityScore}
                    </td>
                    <td className={`text-center p-4 font-medium ${trackingScore !== undefined ? getScoreColor(trackingScore) : 'text-muted-foreground'}`}>
                      {trackingScore !== undefined ? `${trackingScore}%` : 'N/A'}
                    </td>
                    <td className="text-center p-4">
                      {criticalCount > 0 ? (
                        <span className="flex items-center justify-center gap-1 text-red-400">
                          <AlertTriangle className="h-4 w-4" />
                          {criticalCount}
                        </span>
                      ) : (
                        <span className="text-green-400">{site.recommendations.length}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Radar Chart */}
      {data.sites.length >= 2 && data.sites.length <= 5 && (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Multi-Metric Comparison</h2>
          <div className="bg-secondary/30 rounded-lg p-6">
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#333" />
                  <PolarAngleAxis dataKey="metric" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                  {data.sites.slice(0, 5).map((site, i) => (
                    <Radar
                      key={site.id}
                      name={site.name}
                      dataKey={site.name}
                      stroke={radarColors[i]}
                      fill={radarColors[i]}
                      fillOpacity={0.2}
                    />
                  ))}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Performance Chart */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Performance Scores</h2>
        <div className="bg-secondary/30 rounded-lg p-6">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} stroke="#666" />
                <YAxis type="category" dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [`${value}/100`, 'Score']}
                  labelFormatter={(label) => performanceData.find(d => d.name === label)?.fullName || label}
                />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {performanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.score)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* SEO Chart */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">SEO Scores</h2>
        <div className="bg-secondary/30 rounded-lg p-6">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={seoData} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} stroke="#666" />
                <YAxis type="category" dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [`${value}/100`, 'Score']}
                  labelFormatter={(label) => seoData.find(d => d.name === label)?.fullName || label}
                />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {seoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.score)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Tracking Matrix */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Tracking Coverage</h2>
        <div className="bg-secondary/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-medium text-muted-foreground">Site</th>
                <th className="text-center p-4 font-medium text-muted-foreground">GTM</th>
                <th className="text-center p-4 font-medium text-muted-foreground">GA4</th>
                <th className="text-center p-4 font-medium text-muted-foreground">FB Pixel</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Performance</th>
                <th className="text-right p-4 font-medium text-muted-foreground">SEO</th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((site) => (
                <tr key={site.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="p-4 font-medium">{site.name}</td>
                  <td className="text-center p-4">
                    {site.tracking.gtm.present ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500/50 mx-auto" />
                    )}
                  </td>
                  <td className="text-center p-4">
                    {site.tracking.ga4.present ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                    )}
                  </td>
                  <td className="text-center p-4">
                    {site.tracking.facebookPixel.present ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-orange-500/50 mx-auto" />
                    )}
                  </td>
                  <td className={`text-right p-4 font-medium ${getScoreColor(site.vitals.performanceScore)}`}>
                    {site.vitals.performanceScore}
                  </td>
                  <td className={`text-right p-4 font-medium ${getScoreColor(site.seo.score)}`}>
                    {site.seo.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Summary Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatCard
          label="Best Performance"
          value={Math.max(...data.sites.map(s => s.vitals.performanceScore))}
          site={data.sites.find(s => s.vitals.performanceScore === Math.max(...data.sites.map(s => s.vitals.performanceScore)))?.name}
        />
        <StatCard
          label="Worst Performance"
          value={Math.min(...data.sites.map(s => s.vitals.performanceScore))}
          site={data.sites.find(s => s.vitals.performanceScore === Math.min(...data.sites.map(s => s.vitals.performanceScore)))?.name}
          isLow
        />
        <StatCard
          label="Full Tracking"
          value={data.sites.filter(s => s.tracking.ga4.present && s.tracking.gtm.present && s.tracking.facebookPixel.present).length}
          suffix={`/${data.sites.length} sites`}
        />
        <StatCard
          label="No Tracking"
          value={data.sites.filter(s => !s.tracking.ga4.present && !s.tracking.gtm.present).length}
          suffix=" sites"
          isLow={data.sites.filter(s => !s.tracking.ga4.present && !s.tracking.gtm.present).length > 0}
        />
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  site,
  suffix,
  isLow,
}: {
  label: string;
  value: number;
  site?: string;
  suffix?: string;
  isLow?: boolean;
}) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-3xl font-light mt-1 ${isLow ? 'text-red-400' : ''}`}>
        {value}{suffix}
      </p>
      {site && <p className="text-xs text-muted-foreground mt-1">{site}</p>}
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentKey === sortKey;

  return (
    <th
      className="text-center p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center justify-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${isActive ? 'text-foreground' : 'opacity-50'}`} />
      </div>
    </th>
  );
}
